// Shared modal helpers (same file in admin_frontend and staff_frontend).
//
// The apps use two kinds of modal:
//   1. Bootstrap-managed  - <div class="modal fade"> opened with bootstrap.Modal (or data-bs-toggle).
//                           Bootstrap adds a .modal-backdrop to <body>, puts "modal-open" on <body> and
//                           locks the page scroll. All of that is removed only when Bootstrap itself runs
//                           hide() on a LIVE element.
//   2. State-driven       - <div class="modal fade show" style="display:block"> + a hand-written
//                           <div class="modal-backdrop fade show">, shown/hidden by React state.
//
// What went wrong before:
//   - A Bootstrap modal that was removed from the page while still open (for example a page that swaps to a
//     loading skeleton, or a parent that unmounts the modal during Bootstrap's 300ms show animation) left
//     its backdrop and the "modal-open" scroll lock behind, so the screen stayed dimmed and unclickable.
//   - State-driven modals did not react to Escape or to a click outside the dialog, unlike Bootstrap ones.
//
// hideModalThen()   - close a Bootstrap modal, run a callback once it is really gone.
// installModalSafety() - one global safety net, called once from index.js.

const SHOWN = ".modal.show";
const TRANSITION_FALLBACK_MS = 700;

const bsModal = () => (typeof window !== "undefined" ? window.bootstrap?.Modal : undefined);

// Close the Bootstrap modal `target` (an element or an element id) and call `afterHidden` exactly once when it
// is fully hidden. If it is not open (or Bootstrap refuses to hide it mid-animation) the callback still runs,
// so a "close, then refresh" flow never gets stuck waiting for an event that will not come.
export const hideModalThen = (target, afterHidden) => {
    const el = typeof target === "string" ? document.getElementById(target) : target;
    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        afterHidden?.();
    };
    const Modal = bsModal();
    const instance = el && Modal ? Modal.getInstance(el) : null;
    if (!el || !instance || !el.classList.contains("show")) {
        finish();
        return;
    }
    el.addEventListener("hidden.bs.modal", finish, { once: true });
    instance.hide();
    setTimeout(finish, TRANSITION_FALLBACK_MS);
};

// Remove a leftover backdrop / scroll lock when no modal is open any more.
export const clearStrayModalState = () => {
    if (typeof document === "undefined") return;
    if (document.querySelector(SHOWN)) return; // a modal is genuinely open - leave everything alone
    // Only Bootstrap's own backdrop (appended straight to <body>); a React-drawn one lives inside #root.
    document.querySelectorAll("body > .modal-backdrop").forEach((node) => node.remove());
    const body = document.body;
    if (body.classList.contains("modal-open")) body.classList.remove("modal-open");
    if (body.style.overflow) body.style.removeProperty("overflow");
    if (body.style.paddingRight) body.style.removeProperty("padding-right");
};

// A modal that React draws itself (no Bootstrap instance): find its own close control.
const closeControl = (modalEl) => {
    const candidates = modalEl.querySelectorAll(".btn-close, [data-modal-close], [aria-label='Close']");
    for (const node of candidates) {
        if (!node.disabled) return node;
    }
    return null;
};

const topStateModal = () => {
    const Modal = bsModal();
    const open = Array.from(document.querySelectorAll(SHOWN)).filter((el) => !(Modal && Modal.getInstance(el)));
    return open.length ? open[open.length - 1] : null;
};

let installed = false;

export const installModalSafety = () => {
    if (installed || typeof document === "undefined") return;
    installed = true;

    // 1. A modal node left the page: once the dust settles, drop any backdrop / scroll lock it orphaned.
    let timer = null;
    const schedule = () => {
        clearTimeout(timer);
        timer = setTimeout(clearStrayModalState, 450);
    };
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.removedNodes) {
                if (node.nodeType !== 1) continue;
                if (node.matches?.(".modal, .modal-backdrop") || node.querySelector?.(".modal")) {
                    schedule();
                    return;
                }
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // 2. Escape closes the top-most state-driven modal (Bootstrap ones already do).
    document.addEventListener("keydown", (event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        const modalEl = topStateModal();
        const control = modalEl && closeControl(modalEl);
        if (control) control.click();
    });

    // 3. A click on the dimmed area (the .modal wrapper itself, outside .modal-dialog) closes it too.
    document.addEventListener("mousedown", (event) => {
        const modalEl = event.target instanceof Element && event.target.classList.contains("modal") ? event.target : null;
        if (!modalEl || modalEl !== topStateModal()) return;
        const control = closeControl(modalEl);
        if (control) control.click();
    });
};
